import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Box, IconButton, TextField, CircularProgress, Autocomplete, InputLabel, Stack, Tooltip, Chip } from '@mui/material';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import { CloseOutlined } from '@ant-design/icons';

interface LookupProps {
  // Common props
  lookupID?: string;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  showClearButton?: boolean;
  showAddButton?: boolean;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  allowAdd?: boolean;
  value?: any | null;
  dataSource?: any[];
  addButtonTitle?: string;
  customSx?: any;
  getOptionLabel?: (option: any) => string;
  getOptionKey?: (option: any) => string | number;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  onChange?: (record: any | null) => void;
  onBlur?: () => void;
  handleAddNewRecord?: (newRecord: any) => void;
  handleClear?: (e: React.MouseEvent) => void;
  autoFocus?: boolean;
  autoOpen?: boolean;

  // Multi-select specific props
  multiple?: boolean;
  selectedOptions?: any[];
  disableCloseOnSelect?: boolean;
  renderTags?: (value: any[], getTagProps: any) => React.ReactNode;
  labelAbove?: boolean;
  isComeFromFilter?: boolean;
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: any, state: any) => React.ReactNode;
  filterOptions?: (options: any[], state: any) => any[];
  tabIndex?: number;
  maxChipsCount?: number;
  customEndAdornment?: React.ReactNode;
}

const Lookup = ({
  lookupID,
  label,
  placeholder,
  size = 'medium',
  fullWidth = true,
  showClearButton = true,
  showAddButton = true,
  loading = false,
  disabled = false,
  required = false,
  error = false,
  helperText,
  allowAdd,
  value,
  dataSource,
  addButtonTitle,
  customSx,
  getOptionLabel,
  getOptionKey,
  isOptionEqualToValue,
  onChange,
  onBlur,
  handleAddNewRecord,
  handleClear,
  autoFocus = false,
  autoOpen = false,

  // Multi-select props
  multiple = false,
  selectedOptions = [],
  disableCloseOnSelect = false,
  labelAbove = true,
  isComeFromFilter = false,
  renderOption,
  filterOptions,
  maxChipsCount,
  tabIndex,
  customEndAdornment
}: LookupProps) => {
  const [open, setOpen] = useState(false);
  const [singleSelectOpen, setSingleSelectOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chipsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        requestAnimationFrame(() => {
          const newWidth = Math.round(entry.contentRect.width);
          setContainerWidth((prev) => (Math.abs(prev - newWidth) > 3 ? newWidth : prev));
        });
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const { displayChips, remainingCount } = useMemo(() => {
    if (!multiple || !selectedOptions || selectedOptions.length === 0) {
      return { displayChips: [], remainingCount: 0 };
    }

    if (maxChipsCount !== undefined && maxChipsCount > 0) {
      if (selectedOptions.length <= maxChipsCount) {
        return { displayChips: selectedOptions, remainingCount: 0 };
      } else {
        return {
          displayChips: selectedOptions.slice(0, maxChipsCount),
          remainingCount: selectedOptions.length - maxChipsCount
        };
      }
    }

    const hasClearButton = showClearButton && (isComeFromFilter || !required);

    const paddingAndIcons = 70 + (hasClearButton ? 25 : 0) + (showAddButton && allowAdd ? 25 : 0);

    const availableWidth = containerWidth - paddingAndIcons;

    const chipPadding = 30;
    const chipGap = 4;
    const charWidth = 8;
    const plusChipWidth = 60;

    let usedWidth = 0;
    const displayChips = [];

    for (let i = 0; i < selectedOptions.length; i++) {
      const option = selectedOptions[i];
      const label = getOptionLabel?.(option) || '';

      const textWidth = label.length * charWidth;
      const chipWidth = textWidth + chipPadding;

      const remainingItems = selectedOptions.length - i - 1;
      const needsPlusChip = remainingItems > 0;

      const totalNeededWidth = usedWidth + chipWidth + (needsPlusChip ? plusChipWidth : 0);

      if (totalNeededWidth <= availableWidth) {
        displayChips.push(option);
        usedWidth += chipWidth + chipGap;
      } else {
        // We've exceeded the limit, show +X for remaining items
        const remainingCount = selectedOptions.length - i;
        return { displayChips, remainingCount };
      }
    }

    return { displayChips, remainingCount: 0 };
  }, [selectedOptions, multiple, getOptionLabel, containerWidth, showClearButton, isComeFromFilter, required, showAddButton, allowAdd, maxChipsCount]);

  // Custom renderTags function with character limit
  const customRenderTags = (value: any[], getTagProps: any) => {
    if (!multiple) return null;

    return (
      <Box
        ref={chipsContainerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          minWidth: 0,
          maxWidth: 'calc(100% - 24px)',
          overflow: 'hidden'
        }}
      >
        {displayChips.map((option, index) => (
          <Chip
            color="primary"
            {...getTagProps({ index })}
            key={index}
            label={getOptionLabel?.(option) || ''}
            size="small"
            sx={{
              maxWidth: remainingCount > 0 ? 'calc(100% - 38px)' : '100%',
              minWidth: 0,
              flexShrink: 1,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                px: 0.8
              }
            }}
          />
        ))}
        {remainingCount > 0 && (
          <Chip
            color="primary"
            label={`+${remainingCount}`}
            size="small"
            sx={{ ml: 0.5, flexShrink: 0, '& .MuiChip-label': { px: 0.8 } }}
          />
        )}
      </Box>
    );
  };

  // Handle option selection for both single and multi-select
  const handleChange = (event: any, newValue: any) => {
    // For single select, just pass the value
    onChange?.(newValue);
  };

  // Get option styles for highlighting selected options in multi-select
  const getOptionStyles = (option: any) => {
    if (!multiple) return {};

    const isSelected = selectedOptions?.some((selected) => isOptionEqualToValue?.(option, selected));

    return isSelected
      ? {
        backgroundColor: '#e3f2fd',
        cursor: 'pointer'
      }
      : { cursor: 'pointer' };
  };

  // Check if there is any value selected
  const hasSelection = useMemo(() => {
    if (multiple) {
      return selectedOptions && selectedOptions.length > 0;
    } else {
      return value !== null && value !== undefined && value !== '';
    }
  }, [multiple, selectedOptions, value]);

  // Handle autofocus and auto-open
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
        if (autoOpen) {
          if (multiple) {
            setOpen(true);
          } else {
            setSingleSelectOpen(true);
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, autoOpen, multiple]);

  // Resolve unique key for each option to ensure React list stability and avoid duplicate key warnings
  const resolveOptionKey = (option: any): string | number => {
    if (getOptionKey) {
      return getOptionKey(option);
    }
    if (option === null || option === undefined) {
      return '';
    }
    if (typeof option !== 'object') {
      return String(option);
    }

    const directId =
      option.municipalityID ??
      option.cityID ??
      option.countyID ??
      option.stateID ??
      option.userID ??
      option.roleID ??
      option.id ??
      option.value ??
      option.key;

    if (directId !== undefined && directId !== null) {
      return String(directId);
    }

    const idProp = Object.keys(option).find((k) => k.toLowerCase().endsWith('id'));
    if (idProp && option[idProp] !== undefined && option[idProp] !== null) {
      return String(option[idProp]);
    }

    if (getOptionLabel) {
      const label = getOptionLabel(option);
      if (label) return label;
    }

    return option.label ?? option.name ?? String(option);
  };

  return (
    <Box ref={containerRef} sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Stack sx={{ flex: 1, gap: labelAbove ? 0.5 : 0 }}>
        {labelAbove && label && (
          <InputLabel htmlFor={lookupID} required={required}>
            {label}
          </InputLabel>
        )}
        <Autocomplete
          // Multi-select specific props
          autoFocus={autoFocus}
          disableClearable
          multiple={multiple}
          disableCloseOnSelect={multiple ? disableCloseOnSelect : false}
          open={multiple ? open : singleSelectOpen}
          onOpen={multiple ? () => setOpen(true) : () => setSingleSelectOpen(true)}
          onClose={multiple ? () => setOpen(false) : () => setSingleSelectOpen(false)}
          renderTags={multiple ? customRenderTags : undefined}
          // Common props
          id={lookupID}
          options={dataSource || []}
          value={multiple ? selectedOptions : value}
          onChange={handleChange}
          onBlur={onBlur}
          size={size}
          loading={loading}
          disabled={disabled}
          getOptionKey={resolveOptionKey}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          filterOptions={filterOptions}
          slotProps={{
            popupIndicator: {
              sx: {
                p: 0,
                mr: 0
              },
              tabIndex: -1
            }
          }}
          renderOption={renderOption || ((props, option, state) => {
            const { key, ...otherProps } = props as any;
            const uniqueKey =
              option && typeof option === 'object'
                ? (option.municipalityID ??
                   option.cityID ??
                   option.countyID ??
                   option.stateID ??
                   option.userID ??
                   option.roleID ??
                   option.id ??
                   option.value ??
                   (state?.index !== undefined ? `${key}-${state.index}` : key))
                : (state?.index !== undefined ? `${key}-${state.index}` : key);

            return (
              <li key={uniqueKey} {...otherProps} style={getOptionStyles(option)}>
                {getOptionLabel?.(option)}
              </li>
            );
          })}
          renderInput={(params) => (
            <TextField
              {...params}
              label={!labelAbove ? label : ''}
              required={required}
              placeholder={hasSelection ? '' : placeholder}
              fullWidth
              error={error}
              helperText={helperText}
              inputRef={inputRef}
              onFocus={(e) => {
                if (autoOpen && !disabled) {
                  if (multiple) {
                    setOpen(true);
                  } else {
                    setSingleSelectOpen(true);
                  }
                }
                if (params.inputProps?.onFocus) {
                  params.inputProps.onFocus(e as any);
                }
              }}
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  ...(tabIndex !== undefined && { tabIndex })
                },
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {customEndAdornment}
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {/* Clear Button */}
                      {showClearButton && (isComeFromFilter || !required) && hasSelection && (
                        <Tooltip title="Clear">
                          <IconButton
                            color="default"
                            onClick={handleClear}
                            disabled={disabled}
                            tabIndex={-1}
                            sx={{
                              p: 0.5,
                              width: 'auto',
                              height: 'auto'
                            }}
                          >
                            <CloseOutlined style={{ fontSize: 'small' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {showAddButton && allowAdd && (
                        <Tooltip title={addButtonTitle}>
                          <IconButton
                            color="primary"
                            onClick={handleAddNewRecord}
                            disabled={disabled}
                            tabIndex={-1}
                            sx={{
                              p: 0.5,
                              width: 'auto',
                              height: 'auto',
                              mr: -0.25
                            }}
                          >
                            <PlusOutlined style={{ fontSize: 'small' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {params.InputProps.endAdornment}
                    </Box>
                  )
                }
              }}
            />
          )}
          sx={{
            ...customSx,
            '& .MuiAutocomplete-inputRoot': {
              flexWrap: 'nowrap',
              overflow: 'hidden'
            }
          }}
        />
      </Stack>
    </Box>
  );
};

export default Lookup;
